import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Button, 
  Radio, 
  Switch, 
  Drawer, 
  Table, 
  Tag, 
  Tooltip, 
  Divider, 
  Card, 
  Space, 
  Row, 
  Col, 
  Typography, 
  message,
  Checkbox,
  InputNumber,
  Empty,
  Tree
} from 'antd';
import { 
  Info, 
  Search, 
  Plus, 
  Trash2, 
  ChevronLeft, 
  Database, 
  Settings, 
  Save,
  AlertCircle,
  ArrowRight,
  Filter,
  Eye,
  LayoutGrid,
  ChevronRight,
  FolderOpen,
  X
} from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

interface Dataset {
  id: string;
  name: string;
  englishName: string;
  owner: string;
  type: 'fact' | 'dimension';
  fields: Field[];
}

interface FilterCondition {
  id: string;
  type: 'condition';
  field?: string;
  operator?: string;
  value?: any;
}

interface FilterGroupData {
  id: string;
  type: 'group';
  logic: 'AND' | 'OR';
  children: (FilterCondition | FilterGroupData)[];
}

const FilterNode = ({ 
  node, 
  isRoot = false, 
  onAddCondition, 
  onAddGroup, 
  onRemove, 
  onUpdate,
  availableFields 
}: { 
  node: FilterCondition | FilterGroupData, 
  isRoot?: boolean,
  onAddCondition: (parentId: string) => void,
  onAddGroup: (parentId: string) => void,
  onRemove: (id: string) => void,
  onUpdate: (id: string, data: any) => void,
  availableFields: Field[]
}) => {
  if (node.type === 'condition') {
    return (
      <div className="flex items-center gap-2 mb-3 group relative">
        <Select 
          placeholder="选择字段" 
          style={{ width: 200 }}
          value={node.field}
          onChange={val => onUpdate(node.id, { field: val })}
          className="rounded-lg"
        >
          {availableFields.map(f => (
            <Select.Option key={f.name} value={f.name}>
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-[10px] font-mono border border-slate-200 px-1 rounded">abc</span>
                <span>{f.comment}</span>
              </div>
            </Select.Option>
          ))}
        </Select>
        <Select 
          defaultValue="等于" 
          style={{ width: 100 }}
          value={node.operator}
          onChange={val => onUpdate(node.id, { operator: val })}
          className="rounded-lg"
        >
          <Select.Option value="等于">等于</Select.Option>
          <Select.Option value="不等于">不等于</Select.Option>
          <Select.Option value="包含">包含</Select.Option>
          <Select.Option value="不包含">不包含</Select.Option>
        </Select>
        <Select 
          placeholder="选择值" 
          style={{ flex: 1 }}
          value={node.value}
          onChange={val => onUpdate(node.id, { value: val })}
          className="rounded-lg"
        >
          <Select.Option value="v1">示例值1</Select.Option>
        </Select>
        <Button 
          type="text" 
          size="small" 
          icon={<X size={16} />} 
          className="text-slate-400 hover:text-red-500 flex items-center justify-center"
          onClick={() => onRemove(node.id)}
        />
      </div>
    );
  }

  return (
    <div className={`relative flex gap-4 ${isRoot ? '' : 'mt-4'}`}>
      {/* Logical Operator Column */}
      <div className="flex flex-col items-center relative">
        <Select 
          size="small" 
          value={node.logic} 
          onChange={val => onUpdate(node.id, { logic: val })}
          className="w-14 z-10"
          variant="outlined"
        >
          <Select.Option value="AND">且</Select.Option>
          <Select.Option value="OR">或</Select.Option>
        </Select>
        
        {/* Vertical Line for children */}
        <div className="absolute top-4 bottom-4 left-1/2 w-px border-l border-slate-200 -z-0" />
      </div>
      
      {/* Children Column */}
      <div className="flex-1">
        {node.children.map((child, index) => (
          <div key={child.id} className="relative flex items-start">
            {/* Horizontal Connector Line */}
            <div className="absolute -left-4 top-4 w-4 h-px bg-slate-200" />
            
            <div className="flex-1">
              <FilterNode 
                node={child} 
                onAddCondition={onAddCondition}
                onAddGroup={onAddGroup}
                onRemove={onRemove}
                onUpdate={onUpdate}
                availableFields={availableFields}
              />
            </div>
          </div>
        ))}
        
        {/* Actions Row */}
        <div className="relative flex items-center gap-4 mt-2 ml-2">
          {/* Horizontal Connector for Actions */}
          <div className="absolute -left-6 top-1/2 w-6 h-px border-t border-dashed border-slate-300" />
          
          <Button 
            type="link" 
            size="small" 
            icon={<Plus size={14} />} 
            className="p-0 flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700"
            onClick={() => onAddCondition(node.id)}
          >
            添加条件
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<Plus size={14} />} 
            className="p-0 flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700"
            onClick={() => onAddGroup(node.id)}
          >
            添加关系
          </Button>
        </div>
      </div>
    </div>
  );
};

interface Field {
  name: string;
  type: string;
  comment: string;
  source?: string;
}

const mockDatasets: Dataset[] = [
  {
    id: 'ds_1',
    name: '人员统计信息指标NBV',
    englishName: 'std_sc01_heat_boiler_in...',
    owner: '张三',
    type: 'fact',
    fields: [
      { name: 'heat_source_id', type: 'string', comment: '热源ID' },
      { name: 'ts', type: 'timestamp', comment: '时间戳' },
      { name: 'dt', type: 'string', comment: '分区日期' },
    ]
  },
  {
    id: 'ds_2',
    name: '锅炉运行数据-标准模型',
    englishName: 'std_sc01_heatboiler_ins...',
    owner: 'lf0407',
    type: 'fact',
    fields: [
      { name: 'boiler_id', type: 'string', comment: '锅炉ID' },
      { name: 'temp', type: 'double', comment: '温度' },
    ]
  },
  {
    id: 'ds_3',
    name: '锅炉基础信息',
    englishName: 'stdsc01heatboilerinfo',
    owner: 'lf',
    type: 'dimension',
    fields: [
      { name: 'id', type: 'string', comment: 'ID' },
      { name: 'name', type: 'string', comment: '名称' },
    ]
  },
  {
    id: 'ds_4',
    name: '热源基础信息',
    englishName: 'stdsc01heatsourceinfo',
    owner: 'lf',
    type: 'dimension',
    fields: [
      { name: 'id', type: 'string', comment: 'ID' },
      { name: 'name', type: 'string', comment: '名称' },
    ]
  },
  {
    id: 'ds_5',
    name: '锅炉运行数据',
    englishName: 'stdsc01heatboilerinstd...',
    owner: 'as',
    type: 'fact',
    fields: []
  },
  {
    id: 'ds_6',
    name: '累计产热d',
    englishName: 'dddddddd',
    owner: 'dd',
    type: 'fact',
    fields: []
  }
];

interface DetailAccelerationConfigProps {
  onBack?: () => void;
  onSave?: (values: any) => void;
}

export default function DetailAccelerationConfig({ onBack, onSave }: DetailAccelerationConfigProps) {
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState<Dataset[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [previewSearch, setPreviewSearch] = useState('');
  const [filterData, setFilterData] = useState<FilterGroupData>({
    id: 'root',
    type: 'group',
    logic: 'AND',
    children: [
      { id: 'c1', type: 'condition', field: undefined, operator: '等于', value: undefined }
    ]
  });

  const updateFilterData = (action: 'addCondition' | 'addGroup' | 'remove' | 'update', id: string, payload?: any) => {
    const newData = JSON.parse(JSON.stringify(filterData));
    
    const findAndExecute = (current: FilterGroupData): boolean => {
      if (action === 'addCondition' && current.id === id) {
        current.children.push({ id: Math.random().toString(36).substr(2, 9), type: 'condition', operator: '等于' });
        return true;
      }
      if (action === 'addGroup' && current.id === id) {
        current.children.push({ 
          id: Math.random().toString(36).substr(2, 9), 
          type: 'group', 
          logic: 'AND', 
          children: [{ id: Math.random().toString(36).substr(2, 9), type: 'condition', operator: '等于' }] 
        });
        return true;
      }
      if (action === 'update' && current.id === id) {
        Object.assign(current, payload);
        return true;
      }
      
      for (let i = 0; i < current.children.length; i++) {
        const child = current.children[i];
        if (action === 'remove' && child.id === id) {
          current.children.splice(i, 1);
          return true;
        }
        if (child.type === 'condition') {
          if (action === 'update' && child.id === id) {
            Object.assign(child, payload);
            return true;
          }
        } else {
          if (findAndExecute(child)) return true;
        }
      }
      return false;
    };

    findAndExecute(newData);
    setFilterData(newData);
  };

  // Auto-fill owner
  useEffect(() => {
    form.setFieldsValue({
      owner: '管理员',
      strategy: '明细加速',
      updateMethod: 'incremental',
      partitionType: 'day',
      storageCycle: 'permanent'
    });
  }, [form]);

  const handleDatasetSelect = (dataset: Dataset) => {
    if (dataset.type === 'fact') {
      // Only one fact table allowed
      const hasFact = selectedDatasets.some(d => d.type === 'fact');
      if (hasFact && !selectedDatasets.some(d => d.id === dataset.id)) {
        message.warning('仅可选择1 张已关联维表的数据集');
        return;
      }
    }

    const isSelected = selectedDatasets.some(d => d.id === dataset.id);
    let newDatasets: Dataset[];
    if (isSelected) {
      newDatasets = selectedDatasets.filter(d => d.id !== dataset.id);
    } else {
      newDatasets = [...selectedDatasets, dataset];
    }
    updateSelectedDatasets(newDatasets);
  };

  const updateSelectedDatasets = (newDatasets: Dataset[]) => {
    setSelectedDatasets(newDatasets);

    // Update available fields
    const fields: Field[] = [];
    newDatasets.forEach(ds => {
      ds.fields.forEach(f => {
        fields.push({ ...f, source: ds.name });
      });
    });
    setAvailableFields(fields);
  };

  const datasetTableColumns = [
    {
      title: '中文名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Dataset) => (
        <div className="flex items-center gap-2">
          <Database size={14} className={record.type === 'fact' ? 'text-blue-500' : 'text-slate-400'} />
          <span>{text}</span>
        </div>
      )
    },
    {
      title: '英文名',
      dataIndex: 'englishName',
      key: 'englishName',
      render: (text: string) => <span className="text-slate-400 text-xs">{text}</span>
    },
    {
      title: '负责人',
      dataIndex: 'owner',
      key: 'owner',
      width: 100,
    }
  ];

  const previewColumns: ColumnsType<Field> = [
    {
      title: '字段名称',
      dataIndex: 'comment',
      key: 'comment',
      render: (text, record) => (
        <Input 
          defaultValue={record.name} 
          className="h-8" 
          onChange={(e) => {
            // Handle field rename logic
          }}
        />
      )
    },
    {
      title: '原始字段名',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <code className="text-xs text-slate-500">{text}</code>
    },
    {
      title: '字段类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => <Tag className="m-0 text-[10px]">{type.toUpperCase()}</Tag>
    },
    {
      title: '来源数据集',
      dataIndex: 'source',
      key: 'source',
      render: (source) => <span className="text-slate-400 text-xs">{source}</span>
    }
  ];

  const filteredPreviewFields = availableFields.filter(f => 
    selectedFields.includes(f.name) && 
    (f.name.includes(previewSearch) || f.comment.includes(previewSearch))
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <Button 
            type="text" 
            icon={<ChevronLeft size={20} />} 
            onClick={onBack}
            className="flex items-center justify-center"
          />
          <Divider type="vertical" className="h-6" />
          <div>
            <div className="text-xs text-slate-400">物化方案管理 / 新建物化方案</div>
            <div className="text-base font-bold text-slate-800">明细加速配置</div>
          </div>
        </div>
        <Space>
          <Button onClick={onBack}>取消</Button>
          <Button type="primary" icon={<Save size={16} />} onClick={() => form.submit()}>保存方案</Button>
        </Space>
      </div>

      <Form 
        form={form} 
        layout="vertical" 
        className="p-6"
        onFinish={(values) => {
          console.log('Form values:', values);
          message.success('方案保存成功');
          onSave?.(values);
        }}
      >
        <Row gutter={24}>
          {/* Left Column: Content & Storage */}
          <Col span={17}>
            <div className="space-y-6">
              {/* Content Configuration */}
              <Card 
                title={<div className="flex items-center gap-2"><Database size={18} className="text-blue-500" />内容配置</div>}
                className="shadow-sm rounded-xl border-slate-200"
              >
                <div className="space-y-6">
                  {/* Associated Dataset */}
                  <Form.Item 
                    name="datasets" 
                    label={<span className="font-medium">关联数据集</span>}
                    rules={[{ required: true, message: '请选择物化数据集' }]}
                    extra={<Text type="secondary" className="text-xs">请选择需要预打宽的数据集（事实表+维表），系统自动进行关联。</Text>}
                  >
                    <div 
                      className="border border-slate-200 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:border-blue-400 transition-colors bg-slate-50/50"
                      onClick={() => setDrawerVisible(true)}
                    >
                      <div className="flex flex-wrap gap-2">
                        {selectedDatasets.length > 0 ? (
                          selectedDatasets.map(ds => (
                            <Tag 
                              key={ds.id} 
                              closable 
                              onClose={(e) => {
                                e.stopPropagation();
                                handleDatasetSelect(ds);
                              }}
                              color={ds.type === 'fact' ? 'blue' : 'default'}
                              className="m-0"
                            >
                              {ds.name}({ds.englishName})
                            </Tag>
                          ))
                        ) : (
                          <span className="text-slate-400">请选择物化数据集</span>
                        )}
                      </div>
                      <Search size={16} className="text-slate-400" />
                    </div>
                  </Form.Item>

                  {/* Field Selection */}
                  <Form.Item 
                    name="fields" 
                    label={<span className="font-medium">字段选择</span>}
                    rules={[{ required: true, message: '至少选择 1 个字段' }]}
                    extra={<Text type="secondary" className="text-xs">建议选择后续汇总加速需要用到的字段，避免冗余。</Text>}
                  >
                    <Select
                      mode="multiple"
                      placeholder="请选择需要保留在明细宽表中的字段"
                      className="w-full"
                      onChange={(vals) => setSelectedFields(vals)}
                      value={selectedFields}
                      maxTagCount="responsive"
                    >
                      {availableFields.map(f => (
                        <Select.Option key={f.name} value={f.name}>
                          {`${f.comment}(${f.name}) [${f.type}]`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {/* Data Filter */}
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                      <div className="flex items-center gap-2">
                        <Filter size={18} className="text-blue-500" />
                        <span className="font-bold text-slate-700">数据筛选</span>
                        <Tooltip title="开启后配置筛选条件，仅将满足条件的数据落入物化表。">
                          <Info size={14} className="text-slate-400 cursor-help" />
                        </Tooltip>
                      </div>
                      <Switch checked={showFilter} onChange={setShowFilter} size="small" />
                    </div>
                    
                    {showFilter && (
                      <div className="pl-2">
                        <FilterNode 
                          node={filterData} 
                          isRoot={true}
                          onAddCondition={(pid) => updateFilterData('addCondition', pid)}
                          onAddGroup={(pid) => updateFilterData('addGroup', pid)}
                          onRemove={(id) => updateFilterData('remove', id)}
                          onUpdate={(id, data) => updateFilterData('update', id, data)}
                          availableFields={availableFields}
                        />
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Eye size={16} className="text-slate-500" />
                        <span className="font-bold text-slate-700 text-sm">加速物化表预览</span>
                        <code className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded text-slate-600">mv_detail_heat_source_daily</code>
                      </div>
                      <Input 
                        placeholder="检索字段" 
                        size="small" 
                        className="w-48 rounded-md" 
                        prefix={<Search size={12} />} 
                        onChange={e => setPreviewSearch(e.target.value)}
                      />
                    </div>
                    <Table 
                      columns={previewColumns} 
                      dataSource={filteredPreviewFields}
                      pagination={false}
                      size="small"
                      scroll={{ y: 300 }}
                      locale={{ emptyText: <Empty description="请先选择字段" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </div>

                  {/* Update Method */}
                  <Form.Item 
                    name="updateMethod" 
                    label={<span className="font-medium">更新方式</span>}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group className="w-full">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Radio.Button value="incremental" className="w-full h-auto p-4 rounded-xl border-2 flex flex-col items-start gap-1">
                            <div className="font-bold">增量更新 (默认)</div>
                            <div className="text-[11px] text-slate-400 whitespace-normal leading-relaxed">仅计算事实表/维表的新增/变更数据，提升刷新效率。</div>
                          </Radio.Button>
                        </Col>
                        <Col span={12}>
                          <Radio.Button value="full" className="w-full h-auto p-4 rounded-xl border-2 flex flex-col items-start gap-1">
                            <div className="font-bold">全量更新</div>
                            <div className="text-[11px] text-slate-400 whitespace-normal leading-relaxed">每次刷新全量重算宽表，覆盖原有数据。</div>
                          </Radio.Button>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Form.Item>
                </div>
              </Card>

              {/* Storage Configuration */}
              <Card 
                title={<div className="flex items-center gap-2"><Settings size={18} className="text-blue-500" />存储配置</div>}
                className="shadow-sm rounded-xl border-slate-200"
              >
                <div className="space-y-6">
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item 
                        name="partitionField" 
                        label={<span className="font-medium">分区字段</span>}
                        rules={[{ required: true, message: '请选择分区字段' }]}
                        extra={<Text type="secondary" className="text-[11px]">必须选择事实表的时间类型字段，默认 dt。</Text>}
                      >
                        <Select placeholder="选择分区字段">
                          {availableFields.filter(f => f.type.includes('time') || f.name === 'dt').map(f => (
                            <Select.Option key={f.name} value={f.name}>{f.comment}({f.name})</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="partitionType" 
                        label={<span className="font-medium">分区类型</span>}
                        rules={[{ required: true }]}
                      >
                        <Radio.Group>
                          <Radio value="day">按天</Radio>
                          <Radio value="hour">按小时</Radio>
                          <Radio value="month">按月</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item 
                        name="bucketField" 
                        label={<span className="font-medium">分桶字段</span>}
                        rules={[{ required: true }]}
                        extra={<Text type="secondary" className="text-[11px]">建议选择高基数字段，系统默认选择主键。</Text>}
                      >
                        <Select placeholder="选择分桶字段">
                          {availableFields.map(f => (
                            <Select.Option key={f.name} value={f.name}>{f.comment}({f.name})</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="bucketCount" 
                        label={<span className="font-medium">分桶数</span>}
                        rules={[{ required: true }]}
                        extra={<Text type="secondary" className="text-[11px]">支持 1-256，默认由系统自动计算。</Text>}
                      >
                        <InputNumber min={1} max={256} className="w-full" placeholder="自动计算" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item 
                    name="storageCycle" 
                    label={<span className="font-medium">存储周期</span>}
                    rules={[{ required: true }]}
                    extra={
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded text-amber-700 text-[11px] flex gap-2">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                        <div>配置宽表数据保留时长，超过时长的历史分区将自动删除。仅对已分区的时间字段生效。一旦配置清理，过期数据不可恢复。</div>
                      </div>
                    }
                  >
                    <Select>
                      <Select.Option value="permanent">永久存储</Select.Option>
                      <Select.Option value="7d">保留 7 天</Select.Option>
                      <Select.Option value="30d">保留 30 天</Select.Option>
                      <Select.Option value="3m">保留 3 个月</Select.Option>
                      <Select.Option value="6m">保留 6 个月</Select.Option>
                      <Select.Option value="1y">保留 1 年</Select.Option>
                      <Select.Option value="2y">保留 2 年</Select.Option>
                      <Select.Option value="custom">自定义天数</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </Card>
            </div>
          </Col>

          {/* Right Column: Basic Info */}
          <Col span={7}>
            <Card 
              title={<div className="flex items-center gap-2"><Info size={18} className="text-blue-500" />基本信息</div>}
              className="shadow-sm rounded-xl border-slate-200 sticky top-24"
            >
              <div className="space-y-4">
                <Form.Item 
                  name="name" 
                  label={<span className="font-medium">物化名称</span>}
                  rules={[
                    { required: true, message: '请输入物化名称' },
                    { max: 50, message: '长度不超过 50 字符' }
                  ]}
                  extra={<Text type="secondary" className="text-[10px]">规范：策略类型_业务场景/数据集名称_粒度</Text>}
                >
                  <Input placeholder="如：明细加速_供热运行_小网日" />
                </Form.Item>

                <Form.Item 
                  name="strategy" 
                  label={<span className="font-medium">物化策略</span>}
                >
                  <Input disabled className="bg-slate-50" />
                </Form.Item>

                <Form.Item 
                  name="owner" 
                  label={<span className="font-medium">负责人</span>}
                  rules={[{ required: true, message: '请输入负责人' }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item 
                  name="description" 
                  label={<span className="font-medium">描述</span>}
                  rules={[{ max: 200, message: '长度不超过 200 字符' }]}
                >
                  <TextArea 
                    placeholder="填写物化的用途、适用场景，便于后续运维管理" 
                    rows={4} 
                    showCount 
                    maxLength={200}
                  />
                </Form.Item>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Dataset Selection Drawer */}
      <Drawer
        title={<div className="text-lg font-bold">选择数据集</div>}
        placement="right"
        width={1000}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{ body: { padding: 0 } }}
        footer={
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
            <Button onClick={() => setDrawerVisible(false)} className="rounded-lg px-6 h-10">取消</Button>
            <Button type="primary" onClick={() => setDrawerVisible(false)} className="rounded-lg px-6 h-10">确认</Button>
          </div>
        }
      >
        <div className="flex h-full">
          {/* Left Sidebar */}
          <div className="w-64 border-r border-slate-100 p-4 bg-slate-50/30">
            <div className="flex items-center gap-2 mb-4 px-2">
              <LayoutGrid size={18} className="text-blue-600" />
              <span className="font-bold text-slate-700">数据集类目</span>
            </div>
            <Tree
              showIcon
              defaultExpandAll
              defaultSelectedKeys={['all']}
              className="bg-transparent"
              treeData={[
                {
                  title: '全部数据集',
                  key: 'all',
                  icon: <FolderOpen size={16} className="text-blue-400" />,
                  children: [
                    { title: '未分组', key: 'ungrouped' },
                    { title: '供热运行', key: 'heat' },
                    { title: '测试', key: 'test' },
                  ]
                }
              ]}
            />
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col">
            <div className="p-6 border-b border-slate-100">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-blue-800 text-xs leading-relaxed flex gap-3 mb-6">
                <Info size={16} className="shrink-0 mt-0.5" />
                <div>
                  请选择需要预打宽的数据集（事实表+维表），系统自动进行关联，数据集需存在维表关联关系。
                  <div className="mt-1 opacity-80">
                    仅可选择1 张已关联维表的数据集
                  </div>
                </div>
              </div>
              <Input 
                placeholder="请输入数据集名..." 
                prefix={<Search size={16} className="text-slate-400" />} 
                className="h-10 rounded-lg max-w-md" 
              />
            </div>

            <div className="flex-1 overflow-auto p-6">
              <Table
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedDatasets.map(d => d.id),
                  onChange: (selectedRowKeys, selectedRows) => {
                    // This needs careful handling because of the "1 fact table" rule
                    // For simplicity in this mock, we'll just use the handleDatasetSelect logic
                  },
                  onSelect: (record) => handleDatasetSelect(record as Dataset)
                }}
                columns={datasetTableColumns}
                dataSource={mockDatasets}
                rowKey="id"
                pagination={{
                  total: 13,
                  pageSize: 10,
                  showSizeChanger: true,
                  className: "mt-6"
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
